import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast/headless'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import {
    WebhookDelivery,
    WebhookDeliveryOutcome,
    WebhookSubscription,
    WebhookSubscriptionParams,
    webhookEvents,
} from '../../types'
import Alert from '../../ui/Alert'
import Button from '../../ui/Button'
import CodeExample from '../../ui/CodeExample'
import { DataTable } from '../../ui/DataTable'
import FormWrapper from '../../ui/form/FormWrapper'
import { MultiSelect } from '../../ui/form/MultiSelect'
import SwitchField from '../../ui/form/SwitchField'
import TextInput from '../../ui/form/TextInput'
import Heading from '../../ui/Heading'
import { ArchiveIcon, CopyIcon, EditIcon, ForbiddenIcon, PlusIcon } from '../../ui/icons'
import Menu, { MenuItem } from '../../ui/Menu'
import Modal from '../../ui/Modal'
import { PreferencesContext } from '../../ui/PreferencesContext'
import Tag, { TagVariant } from '../../ui/Tag'
import { formatDate } from '../../utils'
import './Transactional.css'

const DELIVERY_POLL_MS = 5000

const outcomeVariants: Record<WebhookDeliveryOutcome, TagVariant> = {
    success: 'success',
    failed: 'error',
    blocked: 'warn',
    deadletter: 'error',
}

function DeliveryList({ subscription }: { subscription: WebhookSubscription }) {
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [project] = useContext(ProjectContext)
    const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([])
    const [selected, setSelected] = useState<WebhookDelivery | undefined>()

    useEffect(() => {
        let active = true
        const load = () => {
            api.webhookSubscriptions.deliveries(project.id, subscription.id)
                .then(results => { if (active) setDeliveries(results) })
                .catch(() => { if (active) setDeliveries([]) })
        }
        load()
        const timer = setInterval(load, DELIVERY_POLL_MS)
        return () => {
            active = false
            clearInterval(timer)
        }
    }, [project.id, subscription.id])

    const detail = selected && deliveries.find(delivery => delivery.id === selected.id)

    return (
        <>
            <div className="webhook-deliveries">
                <DataTable
                    items={deliveries}
                    itemKey={({ item }) => item.id}
                    selectedRow={detail?.id}
                    onSelectRow={item => setSelected(selected?.id === item.id ? undefined : item)}
                    emptyMessage={t('webhook_deliveries_empty')}
                    columns={[
                        { key: 'event', title: t('event') },
                        {
                            key: 'reference',
                            title: t('webhook_reference'),
                            cell: ({ item }) => item.message_id ?? item.address ?? item.event_id,
                        },
                        {
                            key: 'attempt',
                            title: t('webhook_attempt'),
                            cell: ({ item }) => `${item.attempt} / ${item.max_attempts}`,
                        },
                        {
                            key: 'response_status',
                            title: t('status'),
                            cell: ({ item }) => item.response_status ?? '—',
                        },
                        {
                            key: 'duration_ms',
                            title: t('webhook_duration'),
                            cell: ({ item }) => `${item.duration_ms} ms`,
                        },
                        {
                            key: 'outcome',
                            title: t('webhook_outcome'),
                            cell: ({ item }) => (
                                <Tag variant={outcomeVariants[item.outcome]} size="tiny">
                                    {t(`webhook_outcome_${item.outcome}`)}
                                </Tag>
                            ),
                        },
                        {
                            key: 'attempted_at',
                            title: t('time'),
                            cell: ({ item }) => formatDate(preferences, item.attempted_at, 'Pp'),
                        },
                    ]}
                />
            </div>
            {detail && (
                <div className="webhook-delivery-detail">
                    {detail.error && <Alert variant="error" title={t('error')}>{detail.error}</Alert>}
                    <CodeExample title={t('webhook_request_headers')} code={JSON.stringify(detail.request.headers, null, 4)} />
                    <CodeExample title={t('webhook_request_body')} code={detail.request.body} />
                    <CodeExample title={t('webhook_response_body')} code={detail.response_body ?? t('webhook_no_response_body')} />
                </div>
            )}
        </>
    )
}

export default function Transactional() {
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [project] = useContext(ProjectContext)
    const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([])
    const [editing, setEditing] = useState<Partial<WebhookSubscription> | undefined>()
    const [secret, setSecret] = useState<string | undefined>()
    const [viewing, setViewing] = useState<WebhookSubscription | undefined>()

    const reload = useCallback(async () => {
        await api.webhookSubscriptions.all(project.id)
            .then(setSubscriptions)
            .catch(() => setSubscriptions([]))
    }, [project.id])

    useEffect(() => { void reload() }, [reload])

    const handleSave = async ({ url, events, enabled }: WebhookSubscriptionParams) => {
        const params = { url, events: events?.length ? events : null, enabled }
        if (editing?.id) {
            await api.webhookSubscriptions.update(project.id, editing.id, params)
        } else {
            const created = await api.webhookSubscriptions.create(project.id, params)
            setSecret(created.secret)
        }
        await reload()
        setEditing(undefined)
    }

    const handleToggle = async (subscription: WebhookSubscription) => {
        await api.webhookSubscriptions.update(project.id, subscription.id, { enabled: !subscription.enabled })
        await reload()
    }

    const handleDelete = async (subscription: WebhookSubscription) => {
        if (!confirm(t('delete_webhook_confirmation'))) return
        await api.webhookSubscriptions.delete(project.id, subscription.id)
        await reload()
    }

    const handleCopySecret = async (value: string) => {
        await navigator.clipboard.writeText(value)
        toast.success(t('copied_webhook_secret'))
    }

    return (
        <>
            <Heading
                title={t('webhook_subscriptions')}
                size="h3"
                actions={
                    <Button
                        variant="primary"
                        icon={<PlusIcon />}
                        size="small"
                        onClick={() => setEditing({ enabled: true })}
                    >{t('create_webhook')}</Button>
                }
            >{t('webhook_subscriptions_description')}</Heading>
            <DataTable
                items={subscriptions}
                itemKey={({ item }) => item.id}
                onSelectRow={setViewing}
                emptyMessage={t('webhook_no_subscriptions')}
                columns={[
                    { key: 'url', title: t('url') },
                    {
                        key: 'events',
                        title: t('events'),
                        cell: ({ item }) => item.events?.length ? item.events.join(', ') : t('webhook_all_events'),
                    },
                    {
                        key: 'enabled',
                        title: t('webhook_enabled'),
                        cell: ({ item }) => (
                            <Tag variant={item.enabled ? 'success' : 'plain'} size="tiny">
                                {item.enabled ? t('webhook_enabled') : t('webhook_disabled')}
                            </Tag>
                        ),
                    },
                    {
                        key: 'created_at',
                        title: t('created_at'),
                        cell: ({ item }) => formatDate(preferences, item.created_at, 'Pp'),
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => (
                            <div className="webhook-row-actions" onClick={event => event.stopPropagation()}>
                                <Menu size="small">
                                    <MenuItem icon={<EditIcon />} onClick={() => setEditing(item)}>
                                        {t('edit')}
                                    </MenuItem>
                                    <MenuItem icon={<ForbiddenIcon />} onClick={async () => await handleToggle(item)}>
                                        {item.enabled ? t('webhook_disable') : t('webhook_enable')}
                                    </MenuItem>
                                    <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleDelete(item)}>
                                        {t('delete')}
                                    </MenuItem>
                                </Menu>
                            </div>
                        ),
                    },
                ]}
            />

            <Modal
                title={editing?.id ? t('update_webhook') : t('create_webhook')}
                open={Boolean(editing)}
                onClose={() => setEditing(undefined)}
            >
                {editing && (
                    <FormWrapper<WebhookSubscriptionParams>
                        onSubmit={handleSave}
                        defaultValues={{
                            url: editing.url,
                            events: editing.events ?? [],
                            enabled: editing.enabled ?? true,
                        }}
                        submitLabel={editing.id ? t('update_webhook') : t('create_webhook')}
                    >
                        {form => (
                            <>
                                <TextInput.Field
                                    form={form}
                                    name="url"
                                    label={t('webhook_url')}
                                    subtitle={t('webhook_url_subtitle')}
                                    required
                                />
                                <MultiSelect.Field
                                    form={form}
                                    name="events"
                                    label={t('webhook_events')}
                                    subtitle={t('webhook_events_subtitle')}
                                    options={[...webhookEvents]}
                                />
                                <SwitchField
                                    form={form}
                                    name="enabled"
                                    label={t('webhook_enabled')}
                                    subtitle={t('webhook_enabled_subtitle')}
                                />
                            </>
                        )}
                    </FormWrapper>
                )}
            </Modal>

            <Modal
                title={t('webhook_secret')}
                open={Boolean(secret)}
                onClose={() => setSecret(undefined)}
            >
                <Alert
                    variant="warn"
                    size="small"
                    title={t('webhook_secret_warning_title')}
                    body={t('webhook_secret_warning')}
                />
                <TextInput
                    name="webhook_secret"
                    label={t('webhook_secret')}
                    hideLabel
                    size="small"
                    value={secret ?? ''}
                    onChange={() => {}}
                    suffix={
                        <Button
                            icon={<CopyIcon />}
                            size="small"
                            variant="plain"
                            aria-label={t('copy')}
                            onClick={async () => await handleCopySecret(secret!)}
                        />
                    }
                />
            </Modal>

            <Modal
                title={t('webhook_deliveries')}
                description={viewing?.url}
                open={Boolean(viewing)}
                onClose={() => setViewing(undefined)}
                size="large"
            >
                {viewing && <DeliveryList subscription={viewing} />}
            </Modal>
        </>
    )
}
